import { shareEmployeeReferral } from '@api/useQueryCalls';
import ListingItem from '@atoms/UserProfile/ListingItem';
import StatusRow from '@atoms/UserProfile/StatusRow';
import { Text } from '@components/primitives';
import { useToast } from '@hooks';
import { shouldShowError } from '@organisms/HomeTab/homeUtils';
import { useNavigation } from '@react-navigation/native';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { SVG_ICONS } from '@utils/SvgIcons';
import { numberWithDots } from '@utils/utils';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View, Pressable, Linking, FlatList, Platform } from 'react-native';
import { searchEmployees, showWhatsappShareForPic, formatDepartmentAndDesignation } from './nameListingUtils';
import listingStyles from './style';

interface Employee {
  employeeName: string;
  employeeId: number;
  mobileNumber?: string;
  tnxPoint: number | null;
  loginPoint: number | null;
  showLoginPoint: boolean;
  showTxnPoint: boolean;
  department?: string | null;
  designation?: string | null;
}

interface RouteParams {
  shortLink?: string;
  key: string;
}

interface NamesListingProps {
  route: RouteParams;
}

export function shouldShowSubListingItem(
  show: boolean,
  item: Employee,
  t: (key: string) => string,
): JSX.Element | null {
  if (show)
    return (
      <View style={listingStyles.subListingContainer}>
        <ListingItem
          text={t('userProfile.listingItemOne')}
          points={numberWithDots(item.loginPoint || 20000)}
          show={item.showLoginPoint}
          disabled={item.loginPoint === 0}
        />
        <ListingItem
          text={t('userProfile.listingItemTwo')}
          points={numberWithDots(item.tnxPoint || 25000)}
          customContainer={{ marginLeft: item.showLoginPoint ? scale(23) : 0 }}
          show={item.showTxnPoint}
          disabled={item.tnxPoint === 0}
        />
      </View>
    );
  return null;
}

export function shouldShowWhatsappIcon(
  item: Employee,
  isStausAjakanVar: boolean,
  opWhatsAppChat: (items: Employee) => void,
): JSX.Element | null {
  if (showWhatsappShareForPic(isStausAjakanVar, item))
    return (
      <Pressable testID="inviteWhatappIcon" onPress={() => opWhatsAppChat(item)} style={listingStyles.whatsappProfile}>
        {SVG_ICONS.userProfile.inviteWhatapp}
      </Pressable>
    );

  return null;
}

export function shouldShowDesigDepart(isNotStatusAjakanVar: boolean, item: Employee): JSX.Element | null {
  if (isNotStatusAjakanVar)
    return (
      <Text variant="sm-normal" style={listingStyles.desigDepartTextStyle}>
        {formatDepartmentAndDesignation(item?.department ?? '', item?.designation ?? '')}
      </Text>
    );
  return null;
}

export function openWhatsAppChatHandler(
  item: Employee,
  shortLink: string,
  isStausAjakan: boolean,
  sharedReferralLinkPic: Function,
  employeeId: number,
  employerId: number,
  employeeProfile: any,
  t: (key: string) => string,
): void {
  const content = `👋 ${t('userProfile.whatsappContentOne')}\n\n${t('userProfile.whatsappContentTwo')} ${shortLink}`;

  if (((shortLink && Platform.OS === 'android') || Platform.OS === 'web') && item.mobileNumber) {
    const e = AMPLITUDE_CONSTANTS.UserProfile.wa_icon_clicked;

    AmplitudeHelper.logEvent(e.name, {
      [e.attributes.source]: isStausAjakan ? 'pic_page_invite_status' : 'pic_page_employee_list',
      [e.attributes.influencer_role]: 'pic',
      [e.attributes.referred_emp_id]: item.employeeId,
    });

    sharedReferralLinkPic({
      influencerEmployeeId: employeeId,
      influencerUserId: parseInt(employeeProfile?.id ?? 0, 10),
      influencerEmployerId: employerId,
      referredEmployeeId: item.employeeId,
    });

    const url = `https://api.whatsapp.com/send?phone=${item.mobileNumber}&text=${encodeURIComponent(content)}`;
    Linking.openURL(url);
  }
}

export const shouldRenderItem =
  (
    isStausAjakan: boolean,
    openWhatsAppChat: (item: Employee) => void,
    t: (key: string) => string,
  ): (({ item }: { item: Employee }) => JSX.Element) =>
  ({ item }) =>
    (
      <View style={listingStyles.rowContainer}>
        <View>
          <Text variant="sm-normal" color={colors.neutral.charcoal}>
            {item.employeeName}
          </Text>
          {shouldShowDesigDepart(!isStausAjakan, item)}
          {shouldShowSubListingItem(isStausAjakan, item, t)}
        </View>
        {shouldShowWhatsappIcon(item, isStausAjakan, openWhatsAppChat)}
        <View style={listingStyles.separator} />
      </View>
    );

const NamesListing: React.FC<NamesListingProps> = ({ route }) => {
  const shortLink = route?.shortLink ?? '';
  const isStausAjakan = route?.key === 'statusAjakan';

  const navigation = useNavigation();
  const { t } = useTranslation('common');
  const toast = useToast();

  const { employeeId, employerId, employeeProfile, employeesToRefer, employeesReferred, searchText, setInitialData } =
    getStore();

  const { mutate: sharedReferralLinkPic, isError: isSharedError, error: sharedError } = shareEmployeeReferral();

  useEffect(() => {
    shouldShowError(isSharedError, sharedError, toast, t, navigation, setInitialData);
  }, [isSharedError, sharedError]);

  const LISTING_DATA = isStausAjakan ? employeesReferred : employeesToRefer;

  const filteredEmployeesToRefer = searchEmployees(LISTING_DATA, searchText);

  const openWhatsAppChat = (item: Employee) =>
    openWhatsAppChatHandler(
      item,
      shortLink,
      isStausAjakan,
      sharedReferralLinkPic,
      employeeId,
      employerId,
      employeeProfile,
      t,
    );

  return (
    <View style={listingStyles.container}>
      <StatusRow title="Satus Undangan" value={`${filteredEmployeesToRefer.length} Orang`} show={isStausAjakan} />

      <FlatList
        testID={`namelisting-${isStausAjakan}`}
        data={filteredEmployeesToRefer}
        renderItem={shouldRenderItem(isStausAjakan, openWhatsAppChat, t)}
        keyExtractor={(item, index) => index.toString()}
        contentContainerStyle={listingStyles.listContentStyle}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

export default NamesListing;
